import React, { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../../components';
import CustomHeroInput from '../../../../components/CustomHeroInput';
import CustomTextarea from '../../../../components/CustomTextarea';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareCampaign, CampaignEditData } from '../types';
import { partnerRevshareService } from '../../../../api';
import { showPartnerSuccess, showError } from '../../../../utils/toast';

interface EditCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: RevshareCampaign;
  onSave: (data: CampaignEditData) => void;
  onSuccess?: () => void;
}

const EditCampaignModal: React.FC<EditCampaignModalProps> = ({
  isOpen,
  onClose,
  campaign,
  onSave,
  onSuccess,
}) => {
  const { t } = useTranslations();
  const [formData, setFormData] = useState<CampaignEditData>({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [touched, setTouched] = useState<{ name?: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && campaign) {
      // Use existing campaign name or generate from model
      const campaignName = campaign.name || campaign.model?.name || campaign._id?.slice(-4) || '';

      setFormData({
        name: campaignName,
        description: campaign.description || '',
      });
      setErrors({});
      setTouched({});
    }
  }, [isOpen, campaign]);

  const validateName = (name: string): string | undefined => {
    if (!name.trim()) {
      return (
        t('partner.revshare.campaigns.editModal.nameRequired') || 'Название кампании обязательно'
      );
    }
    return undefined;
  };

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, name: value }));
    if (touched.name) {
      setErrors((prev) => ({ ...prev, name: validateName(value) }));
    }
  };

  const handleNameBlur = () => {
    setTouched((prev) => ({ ...prev, name: true }));
    setErrors((prev) => ({ ...prev, name: validateName(formData.name) }));
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, description: e.target.value }));
  };

  const handleSave = async () => {
    const nameError = validateName(formData.name);

    if (nameError) {
      setErrors({ name: nameError });
      setTouched({ name: true });
      return;
    }

    const orderId = campaign._id || campaign.id;
    if (!orderId) {
      showError(
        t('partner.revshare.campaigns.editModal.errorNoId') || 'Не удалось определить ID кампании'
      );
      return;
    }

    setIsLoading(true);
    try {
      await partnerRevshareService.updateOrder(orderId, {
        name: formData.name,
        description: formData.description || undefined,
      });

      showPartnerSuccess(
        t('partner.revshare.campaigns.editModal.success') || 'Изменения успешно сохранены'
      );
      onSave(formData);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error updating campaign:', error);
      showError(
        error?.response?.data?.detail ||
          t('partner.revshare.campaigns.editModal.error') ||
          'Не удалось сохранить изменения'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setFormData({ name: '', description: '' });
    setErrors({});
    setTouched({});
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      classNames={{
        base: 'md:max-w-[37.5rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4">
        <ModalHeader className="flex flex-col gap-3 px-4 md:px-6 pt-6 pb-4">
          <Typography variant="h4" className="font-medium text-auth-ink text-xl">
            {t('partner.revshare.campaigns.editModal.title') || 'Настройки кампании'}
          </Typography>
        </ModalHeader>

        <ModalBody className="px-4 md:px-6 py-3">
          <div className="flex flex-col gap-4">
            <CustomHeroInput
              label={t('partner.revshare.campaigns.editModal.campaignName') || 'Название кампании'}
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={handleNameBlur}
              placeholder="blue sky 06.05.2025"
              error={errors.name}
              touched={touched.name}
            />

            <CustomTextarea
              label={t('partner.revshare.campaigns.editModal.notes') || 'Примечания'}
              value={formData.description}
              onChange={handleNotesChange}
              placeholder={
                t('partner.revshare.campaigns.editModal.notesPlaceholder') ||
                'Добавьте примечания к кампании...'
              }
              minRows={6}
              maxRows={8}
            />
          </div>
        </ModalBody>

        <ModalFooter className="px-6 pb-4 pt-0 h-[4.5rem] flex items-center justify-end">
          <div className="flex gap-2">
            <Button
              className="bg-[rgba(243,18,96,0.2)] h-10 px-4 rounded-3 min-w-[5.625rem]"
              onPress={handleClose}
              isDisabled={isLoading}
            >
              <Typography variant="body2" className="!text-auth-strength-weak font-normal">
                {t('global.buttons.cancel') || 'Отмена'}
              </Typography>
            </Button>
            <Button
              className="bg-partner h-10 px-3.5 py-2.5 rounded-3 min-w-[6.25rem]"
              onPress={handleSave}
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              <Typography variant="body2" className="!text-white font-semibold">
                {t('partner.revshare.campaigns.editModal.save') || 'Сохранить'}
              </Typography>
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default EditCampaignModal;
